import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { format, parseISO, subMonths } from 'date-fns';
import {
  Box,
  Card,
  Pagination,
  Table,
  MaterialSymbol,
  usePagination,
  useUpdateQueryParams,
  capitalizeFirstLetter,
  GetColorStatus,
  RetriggerWebhookRequest,
  TableProps,
  Timestamp,
  Timezone,
  WebhookEvent,
  WebhookNotificationStatus,
  WebhooksList,
} from 'vopay';
import { Button, TableFiltersContainer, Title, TransactionStatus } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useToast } from '#shared/providers/Toast';
import useFetchWebhookNotifications from '#shared/hooks/useApi/useFetchWebhookNotifications';
import useResendWebhook from '#shared/hooks/useApi/useResendWebhook';
import WebhooksTableSkeleton from '../WebhooksTableSkeleton';
import WebhooksHistorySearchForm from './WebhooksHistorySearchForm';
import styles from './WebhooksHistory.module.scss';

type QueryParams = {
  eventType: string;
  notificationStatus: string;
  startDate: string;
  endDate: string;
};

type WebhooksSearchParams = {
  eventType?: string;
  notificationStatus?: WebhookNotificationStatus | 'all';
  timeRange?: [Timestamp, Timestamp];
  timeZone?: string;
};

const PER_PAGE = 20;

type WebhooksHistoryPageProps = {
  timezone: Timezone;
  events: readonly WebhookEvent[];
  /** False hides the Notification Status, Response Code and Response Text columns (see WebhooksHostProps). */
  hasDeliveryDetails: boolean;
  readOnly?: boolean;
};

export default function WebhooksHistoryPage({
  timezone,
  events,
  hasDeliveryDetails,
  readOnly = false,
}: WebhooksHistoryPageProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Webhooks.History' });
  const location = useLocation();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);

  const { fetchingWebhookNotifications, webhookNotifications, totalRecords, fetchWebhookNotifications } =
    useFetchWebhookNotifications();

  const { resendWebhook } = useResendWebhook();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();
  const timeDifference = format(subMonths(new Date(), 1), 'yyyy-MM-dd');
  const currentDateTime = format(new Date(), 'yyyy-MM-dd');
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startDate = useMemo(() => queryParams.get('startDate') || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || '', [queryParams]);
  const eventType = useMemo(() => queryParams.get('eventType') || '', [queryParams]);

  const notificationStatus = useMemo(
    () => (queryParams.get('notificationStatus') || '') as WebhookNotificationStatus | 'all',
    [queryParams],
  );

  const filtersApplied = useMemo(() => {
    return Boolean(
      (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime) ||
        eventType ||
        (notificationStatus && notificationStatus !== 'all'),
    );
  }, [startDate, endDate, eventType, notificationStatus, timeDifference, currentDateTime]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: WebhooksSearchParams = {}) => {
    const defaultValues = {
      StartDate: format(searchParams.timeRange?.[0] ?? parseISO(startDate || timeDifference), 'yyyy-MM-dd'),
      EndDate: format(searchParams.timeRange?.[1] ?? parseISO(endDate || currentDateTime), 'yyyy-MM-dd'),
      ...(searchParams.eventType === 'all' ? {} : { Type: searchParams.eventType }),
      ...(searchParams.notificationStatus === 'all' ? {} : { NotificationStatus: searchParams.notificationStatus }),
      Timezone: timezone,
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    try {
      await fetchWebhookNotifications(defaultValues);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t('ErrorMessages.fetchWebhooks', { defaultValue: 'An error occurred while fetching webhooks history.' });

      alertDialog.error(t('ErrorMessages.error', { defaultValue: 'Error' }), msg);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);

    const searchParams = {
      eventType,
      notificationStatus,
      startDate: format(parseISO(startDate || timeDifference), 'yyyy-MM-dd'),
      endDate: format(parseISO(endDate || currentDateTime), 'yyyy-MM-dd'),
    };

    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  async function retriggerWebhooks(webhook: RetriggerWebhookRequest) {
    const params: RetriggerWebhookRequest = {
      EventID: webhook.EventID,
      Type: webhook.Type,
    };

    const retriggerFailMessage = (
      <strong>
        {t('ToastMessage.retriggerFail', {
          defaultValue: 'Webhook unable to be retriggered. Please try again in a few seconds.',
        })}
      </strong>
    );

    blockingLoader.show();

    try {
      await resendWebhook(params);

      toast.success(
        <strong>{t('ToastMessage.retriggerSuccess', { defaultValue: 'Webhook has been retriggered' })}</strong>,
      );
    } catch (error) {
      toast.neutral(error instanceof Error && error.message ? <strong>{error.message}</strong> : retriggerFailMessage);
    } finally {
      blockingLoader.hide();
    }
  }

  const tableProps: TableProps<WebhooksList> = {
    id: 'webhooksHistoryTable',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'dateSent',
        header: t('Columns.dateSent', { defaultValue: 'Date Sent' }),
        align: 'left',
        field: 'DateSent',
      },
      { id: 'event', header: t('Columns.event', { defaultValue: 'Event' }), align: 'left', field: 'Type' },
      {
        id: 'transactionType',
        header: t('Columns.transactionType', { defaultValue: 'Type' }),
        align: 'left',
        field: 'Module',
      },
      {
        id: 'payloadId',
        header: t('Columns.payloadId', { defaultValue: 'Payload ID' }),
        align: 'left',
        field: 'WebhookID',
      },
      {
        id: 'notificationStatus',
        visible: hasDeliveryDetails,
        header: t('Columns.notificationStatus', { defaultValue: 'Notification Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={GetColorStatus(data.NotificationStatus || '')}
            label={capitalizeFirstLetter(data.NotificationStatus)}
          />
        ),
      },
      {
        id: 'responseStatusCode',
        visible: hasDeliveryDetails,
        header: t('Columns.responseStatusCode', { defaultValue: 'Response Code' }),
        align: 'left',
        field: 'ResponseStatusCode',
      },
      {
        id: 'responseText',
        visible: hasDeliveryDetails,
        header: t('Columns.responseText', { defaultValue: 'Response Text' }),
        align: 'left',
        field: 'ResponseText',
      },
      {
        id: 'payloadStatus',
        header: t('Columns.payloadStatus', { defaultValue: 'Payload Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus status={GetColorStatus(data.Status || '')} label={capitalizeFirstLetter(data.Status)} />
        ),
      },
      {
        id: 'retrigger',
        visible: !readOnly,
        header: t('Columns.action', { defaultValue: 'Action' }),
        align: 'right',
        template: ({ data }) => {
          const isPayloadPending = ['pending', 'in progress'].includes((data.Status || '').toLocaleLowerCase());

          return (
            <Box jc="fe">
              <Button
                key={`retrigger-webhook-${data.EventID}`}
                id={`webhook-${data.EventID}`}
                size="small"
                disabled={isPayloadPending}
                onClick={() => {
                  retriggerWebhooks(data);
                }}
              >
                {t('Columns.retrigger', { defaultValue: 'Retrigger' })}
              </Button>
            </Box>
          );
        },
      },
    ],
    rows: (webhookNotifications || []).map((item): { id: string; data: WebhooksList } => ({
      id: String(item.WebhookID || ''),
      data: item,
    })),
  };

  // Shown before any search has succeeded, as well as for a search with no results.
  const showEmptyState = !fetchingWebhookNotifications && !webhookNotifications?.length;

  return (
    <Box flx="col" gapSize="36px">
      <Card
        style={{
          padding: '16px',
        }}
      >
        <Box flx="col" gap="sm">
          <TableFiltersContainer filtersApplied={filtersApplied}>
            <TableFiltersContainer.Trigger />

            <TableFiltersContainer.Content>
              <WebhooksHistorySearchForm
                events={events}
                hasDeliveryDetails={hasDeliveryDetails}
                eventType={eventType}
                notificationStatus={notificationStatus}
                initialTimeRange={[
                  parseISO(startDate || timeDifference).getTime() as Timestamp,
                  parseISO(endDate || currentDateTime).getTime() as Timestamp,
                ]}
                hasInvalidEndDate={hasInvalidEndDate}
                shouldShowEndDateError={shouldShowEndDateError}
                onShowEndDateErrorChange={setShouldShowEndDateError}
                search={(searchParams) => {
                  const offset = setPageIndex(0);

                  updateQueryParams({
                    eventType: searchParams.eventType || '',
                    notificationStatus: searchParams.notificationStatus || 'all',
                    startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                    endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                  });

                  search(offset, searchParams);
                }}
              />
            </TableFiltersContainer.Content>
          </TableFiltersContainer>
        </Box>

        <div className={styles.webhooksTableScroll}>
          {fetchingWebhookNotifications ? <WebhooksTableSkeleton /> : <Table {...tableProps} />}
        </div>

        {showEmptyState && (
          <div className={styles.webhooksEmptyState}>
            <MaterialSymbol icon="web_asset_off" size="108px" color="#C4C4C4" />

            <Box flx="col" ai="center" jc="center" gapSize="8px" className={styles.webhooksEmptyStateText}>
              <Title level="3">{t('EmptyState.title', { defaultValue: 'No webhook deliveries yet' })}</Title>

              <span className={styles.webhooksEmptyStateDescription}>
                {t('EmptyState.description', {
                  defaultValue:
                    'Once your webhooks are active, every delivery will be logged here with its response and status.',
                })}
              </span>
            </Box>
          </div>
        )}

        <Box width="100%" jc="center" mt="lg" mb="sm">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                const offset = setPageIndex(e.selected);

                const searchParams = {
                  eventType,
                  notificationStatus,
                  startDate: format(new Date(startDate || timeDifference), 'yyyy-MM-dd'),
                  endDate: format(new Date(endDate || currentDateTime), 'yyyy-MM-dd'),
                };

                search(offset, searchParams);
              }}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
}
